import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"
import { googlePlacesFallbackUrl } from "../lib/google_places_fallback_url.mjs"

// Self-heals the Google Places rating skeleton.
//
// The async ProviderGooglePlacesUpdateJob broadcasts its result to a per-chat
// Turbo Stream channel. That broadcast can fail to reach this browser when the
// job was deduped/debounced for a *different* chat viewing the same NPI, when
// the job died, or when the socket dropped. Without this, the loading skeleton
// would hang until the next page load.
//
// This controller polls a cache-backed endpoint that resolves the skeleton
// straight from ProviderGooglePlacesCache (returns a Turbo Stream when the
// lookup is done, or 204 while it's still pending), so the UI no longer depends
// on the broadcast arriving.
export default class extends Controller {
  static values = {
    npi: String,
    addressHash: String,
    url: String,
    delay: { type: Number, default: 4000 },
    interval: { type: Number, default: 3000 },
    maxAttempts: { type: Number, default: 5 },
  }

  connect() {
    this.attempts = 0
    this.timer = setTimeout(() => this.poll(), this.delayValue)
  }

  disconnect() {
    if (this.timer) {
      clearTimeout(this.timer)
      this.timer = null
    }
  }

  async poll() {
    this.attempts += 1

    try {
      const url = googlePlacesFallbackUrl(this.urlValue, this.npiValue, this.addressHashValue)
      const response = await fetch(url, {
        headers: { Accept: "text/vnd.turbo-stream.html" },
      })

      // 200 = lookup resolved (data or known no-match). Applying the stream
      // replaces this element, which disconnects the controller and stops polling.
      if (response.status === 200) {
        const html = await response.text()
        if (html.trim().length > 0) {
          Turbo.renderStreamMessage(html)
        }
        return
      }
    } catch (_e) {
      // Network hiccup - fall through and retry.
    }

    if (this.attempts < this.maxAttemptsValue) {
      this.timer = setTimeout(() => this.poll(), this.intervalValue)
    }
  }
}
